import { Button, Drawer } from '@toggle/design-system';
import { EarningsItemWithEntity } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { WithFooterWrapper } from '~/components/with-footer-wrapper/WithFooterWrapper';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { TabsType } from '~/modules/tab-content/types';
import { queryKeys, queryValues } from '~/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { CalendarEventDrawerHeader } from '~/views/dashboard/calendar-widget/calendar-event-drawer/calendar-event-drawer-header/CalendarEventDrawerHeader';
import {
  getEntityLogo,
  getFallbackEntityTitle,
} from '~/views/dashboard/calendar-widget/calendar-event-drawer/calendar-event-drawer-header/utils';
import { EarningsEventDrawerContent } from '~/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/earnings-event-drawer-content/EarningsEventDrawerContent';

import styles from './EarningsDrawer.module.scss';

export const EarningsDrawer = ({
  drawerData,
  onDrawerClose,
}: {
  drawerData?: EarningsItemWithEntity;
  onDrawerClose: () => void;
}) => {
  const { t } = useTranslation(['calendar']);
  const { navigateToPage } = useNavigateToAssetOverview();
  const { params } = useQueryParams();

  const onFullScreenClick = (drawerData: EarningsItemWithEntity) =>
    navigateToPage({
      entityTag: drawerData.entity_tag,
      tab: TabsType.Earnings,
    });

  const getHeaderProps = (data: EarningsItemWithEntity) => ({
    title: data.entity?.name ?? getFallbackEntityTitle(data.entity_tag),
    subtitle: data.entity?.formattedTicker ?? '',
    logoNode: getEntityLogo(data.entity_tag, data.entity),
    dateString: data.reporting_date,
    reported: data.reported,
  });
  const isOpen = params[queryKeys.activeDrawer] === queryValues.earningsDrawer;
  return (
    <>
      {drawerData && (
        <Drawer
          data-testid="earnings-events-drawer"
          isOpen={isOpen}
          handleCloseClick={onDrawerClose}
          headerButton={
            <Button
              variant="empty"
              iconName="Expand"
              label={t('calendar:viewMore')}
              size="xsmall"
              onClick={() => onFullScreenClick(drawerData)}
            />
          }
        >
          <WithFooterWrapper
            isWrappedComponentOpened={isOpen}
            trackingMetadata={{
              source: POSTHOG_TRACKING_SOURCES.EARNINGS_DRAWER,
            }}
          >
            <div className={styles.contentWrapper}>
              <CalendarEventDrawerHeader {...getHeaderProps(drawerData)} />
              <EarningsEventDrawerContent data={drawerData} />
            </div>
          </WithFooterWrapper>
        </Drawer>
      )}
    </>
  );
};
